import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { adminApi } from '@/api/endpoints'
import { Avatar, ChevronIcon, LiveIcon } from '@/components/icons'
import { ErrorState, Skeleton } from '@/components/ui/Feedback'
import { PageHeader } from '@/components/ui/Page'
import { StatStrip } from '@/components/ui/StatStrip'
import { StatusPill } from '@/components/ui/StatusPill'
import { LiveQueueList } from '@/features/live/LiveQueueList'
import { clock, useLiveQueue } from '@/features/live/live'
import { cn } from '@/lib/utils'
import type { Overview } from '@/types/api'

/**
 * The first screen: what needs a person now, then how the whole system is doing.
 *
 * Order matters here. The live queue is on top because it is the only thing
 * with a clock on it; the counts below can wait.
 */
export function OverviewPage() {
  const navigate = useNavigate()
  const queue = useLiveQueue()
  const overview = useQuery({ queryKey: ['overview'], queryFn: adminApi.overview, refetchInterval: 20_000 })
  const data = overview.data
  const by = data?.applications.byStatus ?? {}
  const inProgress = (by.queued ?? 0) + (by.in_flight ?? 0)

  return (
    <>
      <PageHeader title="Overview" description="Checks waiting for a person, then every user's applications at a glance." />

      {/* ---- Now ---- */}
      <section className="card mb-5 overflow-hidden">
        <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3.5 sm:px-5">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="relative flex h-2 w-2 shrink-0">
              {queue.items.length > 0 && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-warning opacity-70" />}
              <span className={cn('relative inline-flex h-2 w-2 rounded-full', queue.items.length > 0 ? 'bg-warning' : 'bg-success')} />
            </span>
            <h2 className="truncate text-[15.5px] font-semibold text-ink">
              {queue.items.length === 0 ? 'No checks waiting' : queue.items.length === 1 ? '1 check waiting' : `${queue.items.length} checks waiting`}
            </h2>
          </div>
          <Link to="/live" className="inline-flex shrink-0 items-center gap-1 text-[13px] font-medium text-ink-muted hover:text-ink">
            Live checks
            <ChevronIcon className="h-3.5 w-3.5" />
          </Link>
        </header>
        {queue.items.length > 0 ? (
          <LiveQueueList items={queue.items.slice(0, 4)} />
        ) : (
          <div className="flex items-center gap-3 px-4 py-5 text-[13.5px] text-ink-muted sm:px-5">
            <LiveIcon className="h-5 w-5 shrink-0 text-ink-faint" />
            <p>
              When a run meets a CAPTCHA it shows here with the time left
              {data ? ` (${clock(data.captcha.timeoutSeconds)} each)` : ''}. You or the user can clear it.
            </p>
          </div>
        )}
      </section>

      {overview.isError ? (
        <div className="card">
          <ErrorState title="The overview could not be loaded" onRetry={() => void overview.refetch()} />
        </div>
      ) : (
        <>
          {/* ---- Applications, all users ---- */}
          <StatStrip
            loading={overview.isLoading}
            className="mb-5"
            segments={[
              { key: 'submitted', label: 'Submitted', value: by.submitted ?? 0, tone: 'success', hint: data ? `${data.applications.submittedToday} in the last day` : undefined },
              { key: 'in_progress', label: 'In progress', value: inProgress, tone: 'info', live: inProgress > 0, hint: data ? `${by.queued ?? 0} queued` : undefined },
              { key: 'needs_you', label: 'Needs user', value: by.needs_you ?? 0, tone: 'warning', hint: 'waiting on an answer' },
              { key: 'failed', label: 'Failed', value: by.failed ?? 0, tone: 'danger', hint: data ? `${data.applications.captchaStopped} stopped by a CAPTCHA` : undefined },
            ]}
          />

          <div className="grid gap-5 lg:grid-cols-3">
            <Tile title="CAPTCHAs, last day" loading={overview.isLoading}>
              {data && <Captcha data={data} />}
            </Tile>
            <Tile title="Users" loading={overview.isLoading} to="/users">
              {data && (
                <>
                  <Big value={data.users.total} label="accounts" />
                  <Rows
                    rows={[
                      ['Signed in, last day', data.users.signedInToday],
                      ['New this week', data.users.newThisWeek],
                      ['Finished onboarding', data.users.onboarded ?? 0],
                      ['Auto apply on', data.users.autoApplyOn ?? 0],
                      ...Object.entries(data.users.byPlan ?? {})
                        .filter(([key]) => key !== 'none')
                        .sort(([, a], [, b]) => b - a)
                        .map(([key, count]): [string, number] => [`On ${key.charAt(0).toUpperCase()}${key.slice(1)}`, count]),
                      ['No plan', data.users.byPlan?.none ?? 0],
                    ]}
                  />
                </>
              )}
            </Tile>
            <Tile title="System" loading={overview.isLoading}>
              {data && <System data={data} />}
            </Tile>
          </div>

          {/* ---- Who to help ---- */}
          <section className="card mt-5 overflow-hidden">
            <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3.5 sm:px-5">
              <div className="min-w-0">
                <h2 className="text-[15.5px] font-semibold text-ink">Users a CAPTCHA has stopped</h2>
                <p className="mt-0.5 text-[12.5px] text-ink-muted">Nothing was sent on these. Run them again and clear the check when it comes up.</p>
              </div>
              <Link to="/users?filter=captcha" className="inline-flex shrink-0 items-center gap-1 text-[13px] font-medium text-ink-muted hover:text-ink">
                All
                <ChevronIcon className="h-3.5 w-3.5" />
              </Link>
            </header>
            {overview.isLoading ? (
              <div className="space-y-3 p-5">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : data && data.needsHelp.length > 0 ? (
              <ul className="divide-y divide-line">
                {data.needsHelp.map((user) => (
                  <li key={user.id}>
                    <button type="button" onClick={() => navigate(`/users/${user.id}`)} className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-muted sm:px-5">
                      <Avatar name={user.fullName || user.email} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[14px] font-medium text-ink">{user.fullName || user.email}</span>
                        {user.fullName && <span className="block truncate text-[12.5px] text-ink-muted">{user.email}</span>}
                      </span>
                      <StatusPill tone="danger">
                        <span className="num">{user.captchaStopped}</span> stopped
                      </StatusPill>
                      <ChevronIcon className="h-4 w-4 shrink-0 text-ink-faint" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="px-4 py-6 text-[13.5px] text-ink-muted sm:px-5">Nobody right now. Every run that met a CAPTCHA got through.</p>
            )}
          </section>
        </>
      )}
    </>
  )
}

function Tile({ title, loading, to, children }: { title: string; loading: boolean; to?: string; children: React.ReactNode }) {
  return (
    <section className="card px-4 py-4 sm:px-5">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="label-eyebrow">{title}</h2>
        {to && (
          <Link to={to} aria-label={`Open ${title}`} className="text-ink-faint hover:text-ink">
            <ChevronIcon className="h-4 w-4" />
          </Link>
        )}
      </div>
      {loading ? (
        <div className="space-y-2.5">
          <Skeleton className="h-8 w-24" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      ) : (
        children
      )}
    </section>
  )
}

function Big({ value, label, tone = 'text-ink' }: { value: number | string; label: string; tone?: string }) {
  return (
    <p className="flex items-baseline gap-2">
      <span className={cn('num text-[30px] font-semibold leading-none', tone)}>{value}</span>
      <span className="text-[13px] text-ink-muted">{label}</span>
    </p>
  )
}

function Rows({ rows }: { rows: Array<[string, React.ReactNode]> }) {
  return (
    <dl className="mt-3.5 space-y-2 border-t border-line pt-3">
      {rows.map(([label, value]) => (
        <div key={label} className="flex items-center justify-between gap-3 text-[13px]">
          <dt className="text-ink-muted">{label}</dt>
          <dd className="num font-medium text-ink">{value}</dd>
        </div>
      ))}
    </dl>
  )
}

function Captcha({ data }: { data: Overview }) {
  const { solvedToday, missedToday, timeoutSeconds, handoffEnabled } = data.captcha
  const seen = solvedToday + missedToday
  const rate = seen ? Math.round((solvedToday / seen) * 100) : null
  return (
    <>
      <Big value={rate === null ? '–' : `${rate}%`} label={rate === null ? 'none met yet' : 'cleared in time'} tone={rate === null ? 'text-ink' : rate >= 80 ? 'text-success' : rate >= 50 ? 'text-warning' : 'text-danger'} />
      <Rows
        rows={[
          ['Cleared', solvedToday],
          ['Missed (time ran out)', <span key="m" className={missedToday ? 'text-danger' : undefined}>{missedToday}</span>],
          ['Time allowed', handoffEnabled ? clock(timeoutSeconds) : 'Hand-off is off'],
        ]}
      />
    </>
  )
}

function System({ data }: { data: Overview }) {
  const { applyWorkers, queue } = data.system
  const running = (applyWorkers ?? 0) > 0
  return (
    <>
      <div className="flex items-center gap-2">
        <StatusPill tone={applyWorkers === null ? 'neutral' : running ? 'success' : 'danger'} live={running}>
          {applyWorkers === null ? 'Queue unreachable' : running ? 'Apply worker running' : 'Apply worker not running'}
        </StatusPill>
      </div>
      {applyWorkers === 0 && <p className="mt-2 text-[12.5px] leading-relaxed text-danger">Queued applications will sit until a worker starts.</p>}
      <Rows
        rows={[
          ['Workers connected', applyWorkers ?? '–'],
          ['Runs in progress', queue?.active ?? '–'],
          ['Waiting in the queue', queue ? queue.waiting + queue.delayed : '–'],
        ]}
      />
    </>
  )
}
